import { useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  submissions: any[];
  // Assessment data already loaded for the table, keyed by submission id.
  assessmentMap: any;
  // The table fetches the assessments one by one, so the numbers grow while that runs.
  loading?: boolean;
  onSelectRule?: (rule: string) => void;
  activeRule?: string;
};

const BUCKET_COUNT = 5;

// The rule matters, not the element it hit, so the trailing "Label: …" is cut off.
// Exported because the submission list filters by the same rule.
export const ruleOfFeedback = (content: string) => (content ?? "").toString().replace(/\s*Label:.*$/s, "").trim();

// Overview across all submissions of one exercise: how far the group got and which
// feedback messages came up most often. Everything is derived from the assessments
// that are already loaded for the table, so no additional requests are needed.
export const SubmissionStatisticsComponent = (props: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const assessedEntries = props.submissions
    .map((submission: any) => props.assessmentMap[submission.id])
    .filter((entry: any) => entry !== undefined && entry["status"] === false)
    .map((entry: any) => entry["last_complete_assessment"])
    .filter((assessment: any) => {
      const max = parseFloat(assessment.maxTotalScore);
      const score = parseFloat(assessment.totalscore);
      return !isNaN(max) && max > 0 && !isNaN(score);
    });

  const scores = assessedEntries.map((assessment: any) => parseFloat(assessment.totalscore));
  // Scores are shown in points, not percent, because that is what teachers award and discuss.
  // The maximum can differ per submission, so the most common one is used as the scale.
  const maxCounts: any = {};
  assessedEntries.forEach((assessment: any) => {
    const max = parseFloat(assessment.maxTotalScore);
    maxCounts[max] = (maxCounts[max] ?? 0) + 1;
  });
  const maxScore = Object.keys(maxCounts)
    .map((value) => parseFloat(value))
    .sort((a, b) => maxCounts[b] - maxCounts[a])[0];
  const mixedMaxScores = Object.keys(maxCounts).length > 1;

  const averageScore = scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
  const bestScore = scores.length > 0 ? Math.max(...scores) : 0;
  const worstScore = scores.length > 0 ? Math.min(...scores) : 0;

  const formatScore = (score: number) => score.toFixed(2).replace(".", ",");
  const formatBound = (bound: number) => (Number.isInteger(bound) ? bound.toString() : bound.toFixed(1).replace(".", ","));

  // Distribution in five equal point ranges, the top range includes the full score.
  const bucketWidth = maxScore / BUCKET_COUNT;
  const buckets = new Array(BUCKET_COUNT).fill(0);
  scores.forEach((score: number) => {
    const index = Math.min(Math.floor(score / bucketWidth), BUCKET_COUNT - 1);
    buckets[index] = buckets[index] + 1;
  });
  const largestBucket = Math.max(...buckets, 1);

  // Negative feedback of the grading services. The rule matters, not the element it hit, so
  // the trailing "Label: …" is cut off before counting — otherwise one rule fills the whole list.
  const ruleOf = ruleOfFeedback;
  const feedbackCounts: any = {};
  const feedbackSubmissions: any = {};
  let totalNegative = 0;
  let submissionsWithoutNegative = 0;
  assessedEntries.forEach((assessment: any) => {
    const seenInThisSubmission: any = {};
    let negativeInThisSubmission = 0;
    const gradings = assessment.gradings ?? [];
    gradings.forEach((grading: any) => {
      const feedbacks = grading.feedbacks ?? [];
      feedbacks.forEach((feedback: any) => {
        if (feedback.valuation !== "NEGATIVE") return;
        if (feedback.category === "internal") return;
        const rule = ruleOf((feedback.content ?? "").toString());
        if (rule === "") return;
        negativeInThisSubmission = negativeInThisSubmission + 1;
        totalNegative = totalNegative + 1;
        feedbackCounts[rule] = (feedbackCounts[rule] ?? 0) + 1;
        if (!seenInThisSubmission[rule]) {
          seenInThisSubmission[rule] = true;
          feedbackSubmissions[rule] = (feedbackSubmissions[rule] ?? 0) + 1;
        }
      });
    });
    if (negativeInThisSubmission === 0) {
      submissionsWithoutNegative = submissionsWithoutNegative + 1;
    }
  });

  const averageNegative = assessedEntries.length > 0 ? totalNegative / assessedEntries.length : 0;

  // Sorted by how many submissions are affected, which is the question teachers ask:
  // which mistake did many students make, not which message occurred most often overall.
  const topFeedback = Object.keys(feedbackCounts)
    .map((rule) => ({ content: rule, count: feedbackCounts[rule], submissions: feedbackSubmissions[rule] }))
    .sort((a, b) => b.submissions - a.submissions || b.count - a.count)
    .slice(0, 5);

  return (
    <div className="card my-2">
      <div className="card-content" style={{ paddingBottom: "1rem" }}>
        <button className="button is-small is-light" onClick={() => setExpanded(!expanded)}>
          {t("statisticsToggle")}
        </button>
        {expanded && (
          <div className="mt-3">
            {assessedEntries.length === 0 ? (
              <p className="is-size-7">{props.loading ? t("statisticsLoading") : t("statisticsNoData")}</p>
            ) : (
              <>
                {/* While loading, the count says how far the lookup got, not how many are
                    assessed. Phrasing it as a result made a growing number look like assessments
                    were being created. Everything below is dimmed for the same reason. */}
                <p className="is-size-7">
                  {props.loading
                    ? t("statisticsLoadingProgress", {
                        loaded: assessedEntries.length,
                        total: props.submissions.length,
                      })
                    : t("statisticsAssessedCount", {
                        assessed: assessedEntries.length,
                        total: props.submissions.length,
                      })}
                </p>
                <div style={props.loading ? { opacity: 0.45 } : undefined}>
                <p className="is-size-7">
                  {t("statisticsAverage", { value: formatScore(averageScore), max: formatBound(maxScore) })} ·{" "}
                  {t("statisticsBest", { value: formatScore(bestScore) })} ·{" "}
                  {t("statisticsWorst", { value: formatScore(worstScore) })}
                </p>
                {mixedMaxScores && <p className="is-size-7 has-text-grey">{t("statisticsMixedMax")}</p>}
                <p className="is-size-7">
                  {t("statisticsWithoutNegative", {
                    count: submissionsWithoutNegative,
                    total: assessedEntries.length,
                  })}{" "}
                  · {t("statisticsAverageNegative", { value: averageNegative.toFixed(1).replace(".", ",") })}
                </p>
                <table className="table is-narrow mt-3" style={{ width: "auto" }}>
                  <tbody>
                    {buckets.map((count: number, index: number) => (
                      <tr key={"bucket" + index}>
                        <td className="is-size-7">
                          {formatBound(index * bucketWidth)}
                          {" - "}
                          {formatBound((index + 1) * bucketWidth)}
                        </td>
                        <td>
                          <div
                            style={{
                              backgroundColor: "#35495d",
                              height: "0.8rem",
                              width: Math.round((count / largestBucket) * 200) + "px",
                              minWidth: count > 0 ? "4px" : "0px",
                            }}
                          ></div>
                        </td>
                        <td className="is-size-7">{count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="is-size-7 has-text-weight-semibold mt-2">{t("statisticsTopFeedback")}</p>
                {topFeedback.length === 0 ? (
                  <p className="is-size-7">{t("statisticsNoFeedback")}</p>
                ) : (
                  <ol className="is-size-7" style={{ marginLeft: "1.2rem" }}>
                    {topFeedback.map((entry: any, index: number) => (
                      <li key={"topFeedback" + index}>
                        {/* Clicking filters the list to the affected submissions instead of showing
                            example submissions, which would make an arbitrary few look representative. */}
                        {props.onSelectRule ? (
                          <button
                            type="button"
                            className="button is-ghost is-small p-0 has-text-left"
                            style={{
                              height: "auto",
                              whiteSpace: "normal",
                              textDecoration: props.activeRule === entry.content ? "underline" : "none",
                              fontWeight: props.activeRule === entry.content ? 600 : 400,
                            }}
                            onClick={() => props.onSelectRule && props.onSelectRule(entry.content)}
                            title={t("statisticsFilterByRule") as string}
                          >
                            {entry.content}
                          </button>
                        ) : (
                          entry.content
                        )}{" "}
                        <span className="has-text-grey">
                          (
                          {t("statisticsFeedbackSubmissions", {
                            submissions: entry.submissions,
                            total: assessedEntries.length,
                          })}
                          , {t("statisticsFeedbackCount", { value: entry.count })})
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
